import { useEffect, useState } from 'react';
import AccessDenied from '@/components/AccessDenied';
import { useAuth } from '@/hooks/useAuth';
import {
  COMPLIANCE_PERMISSIONS,
  hasPermission,
} from '@/lib/compliancePermissions';
import {
  usePromotionMaterials,
  type CreatePromotionMaterialDto,
  type PromotionMaterialDto,
  type RecordPromotionReviewDto,
} from '@/hooks/useCompliance';
import {
  PROMOTION_MATERIAL_TYPE_LABELS,
  PROMOTION_MATERIAL_STATUS_LABELS,
  PROMOTION_REVIEW_RESULT_LABELS,
  PROMOTION_REVIEWER_ROLE_LABELS,
  RISK_LEVEL_LABELS,
  formatEnumLabel,
} from '@/lib/enumLabels';

const PROMOTION_MATERIAL_TYPES: readonly PromotionMaterialDto['materialType'][] = [
  'ppt',
  'social_copy',
  'case',
  'video',
  'testimonial',
  'proposal_wording',
  'project_intro',
];

const PROMOTION_REVIEW_STATUSES: readonly PromotionMaterialDto['status'][] = [
  'draft',
  'submitted',
  'under_review',
  'changes_requested',
  'approved',
  'rejected',
  'withdrawn',
  'archived',
];

const PROMOTION_REVIEWER_ROLES: readonly RecordPromotionReviewDto['reviewerRole'][] = [
  'compliance',
  'medical',
  'legal',
];

const NEXT_STATUS_MAP: Readonly<Record<PromotionMaterialDto['status'], readonly ('submit' | 'claim' | 'withdraw' | 'review')[]>> = {
  draft: ['submit', 'withdraw'],
  submitted: ['claim', 'withdraw'],
  under_review: ['review'],
  changes_requested: ['submit', 'withdraw'],
  approved: [],
  rejected: [],
  withdrawn: [],
  archived: [],
};

function fmtDateTime(value?: string): string {
  return value ? new Date(value).toLocaleString('zh-CN') : '—';
}

function riskTone(level?: PromotionMaterialDto['riskLevel']): string {
  if (level === 'critical') return 'bg-red-100 text-red-700';
  if (level === 'high') return 'bg-orange-100 text-orange-700';
  if (level === 'medium') return 'bg-amber-100 text-amber-700';
  if (level === 'low') return 'bg-emerald-100 text-emerald-700';
  return 'bg-slate-100 text-slate-600';
}

export default function PromotionReviewPage() {
  const { permissions } = useAuth();
  const canReadMaterial = hasPermission(permissions, COMPLIANCE_PERMISSIONS.PROMOTION_MATERIAL_READ);
  const canCreateMaterial = hasPermission(permissions, COMPLIANCE_PERMISSIONS.PROMOTION_MATERIAL_CREATE);
  const canSubmitMaterial = hasPermission(permissions, COMPLIANCE_PERMISSIONS.PROMOTION_MATERIAL_SUBMIT);
  const canWithdrawMaterial = hasPermission(permissions, COMPLIANCE_PERMISSIONS.PROMOTION_MATERIAL_WITHDRAW);
  const canReadReview = hasPermission(permissions, COMPLIANCE_PERMISSIONS.PROMOTION_REVIEW_READ);
  const canApprove = hasPermission(permissions, COMPLIANCE_PERMISSIONS.PROMOTION_REVIEW_APPROVE);
  const {
    materials,
    hits,
    reviews,
    loading,
    error,
    fetchMaterials,
    createMaterial,
    submitMaterial,
    claimMaterial,
    withdrawMaterial,
    reviewMaterial,
    fetchHits,
    fetchReviews,
  } = usePromotionMaterials();

  const [statusFilter, setStatusFilter] = useState<PromotionMaterialDto['status'] | ''>('');
  const [typeFilter, setTypeFilter] = useState<PromotionMaterialDto['materialType'] | ''>('');
  const [selectedMaterialId, setSelectedMaterialId] = useState('');
  const [createForm, setCreateForm] = useState<CreatePromotionMaterialDto>({
    title: '',
    materialType: 'social_copy',
    contentText: '',
    contentOssKey: '',
    summary: '',
    projectCode: '',
  });
  const [reviewForm, setReviewForm] = useState<RecordPromotionReviewDto>({
    expectedVersion: 0,
    reviewerRole: 'compliance',
    reviewResult: 'approved',
    comment: '',
  });

  useEffect(() => {
    if (!canReadMaterial) {
      return;
    }
    void fetchMaterials({
      status: statusFilter || undefined,
      materialType: typeFilter || undefined,
    });
  }, [canReadMaterial, statusFilter, typeFilter, fetchMaterials]);

  useEffect(() => {
    if (!selectedMaterialId || !canReadReview) {
      return;
    }
    void fetchHits(selectedMaterialId);
    void fetchReviews(selectedMaterialId);
  }, [selectedMaterialId, canReadReview, fetchHits, fetchReviews]);

  if (!canReadMaterial) {
    return <AccessDenied resource="宣传审核" />;
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const result = await createMaterial({
      ...createForm,
      contentText: createForm.contentText || undefined,
      contentOssKey: createForm.contentOssKey || undefined,
      summary: createForm.summary || undefined,
      projectCode: createForm.projectCode || undefined,
    });
    if (result) {
      setCreateForm({
        title: '',
        materialType: 'social_copy',
        contentText: '',
        contentOssKey: '',
        summary: '',
        projectCode: '',
      });
      void fetchMaterials({
        status: statusFilter || undefined,
        materialType: typeFilter || undefined,
      });
    }
  }

  async function handleMaterialAction(item: PromotionMaterialDto, action: 'submit' | 'claim' | 'withdraw') {
    const dto = { expectedVersion: item.version };
    const result =
      action === 'submit'
        ? await submitMaterial(item.id, dto)
        : action === 'claim'
          ? await claimMaterial(item.id, dto)
          : await withdrawMaterial(item.id, dto);
    if (result) {
      void fetchMaterials({
        status: statusFilter || undefined,
        materialType: typeFilter || undefined,
      });
      if (selectedMaterialId === item.id) {
        void fetchHits(item.id);
        void fetchReviews(item.id);
      }
    }
  }

  async function handleReview(item: PromotionMaterialDto) {
    const result = await reviewMaterial(item.id, {
      ...reviewForm,
      expectedVersion: item.version,
    });
    if (result) {
      setReviewForm((prev) => ({ ...prev, comment: '' }));
      void fetchMaterials({
        status: statusFilter || undefined,
        materialType: typeFilter || undefined,
      });
      if (selectedMaterialId === item.id) {
        void fetchHits(item.id);
        void fetchReviews(item.id);
      }
    }
  }

  const selectedMaterial = materials.find((item) => item.id === selectedMaterialId) ?? null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">宣传材料审核</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            仅允许真实后端状态流转，AI 命中词只能作为审核提示，不可自动审批。
          </p>
        </div>
        <div className="flex gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as PromotionMaterialDto['status'] | '')}
            className="rounded border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">全部状态</option>
            {PROMOTION_REVIEW_STATUSES.map((status) => (
              <option key={status} value={status}>
                {formatEnumLabel(status, PROMOTION_MATERIAL_STATUS_LABELS, '未知状态')}
              </option>
            ))}
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as PromotionMaterialDto['materialType'] | '')}
            className="rounded border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">全部类型</option>
            {PROMOTION_MATERIAL_TYPES.map((item) => (
              <option key={item} value={item}>
                {formatEnumLabel(item, PROMOTION_MATERIAL_TYPE_LABELS, '未知类型')}
              </option>
            ))}
          </select>
        </div>
      </div>

      {canCreateMaterial && (
        <form onSubmit={(e) => { void handleCreate(e); }} className="grid gap-4 rounded-lg border bg-white p-5 shadow-sm lg:grid-cols-2">
          <div className="lg:col-span-2 text-lg font-semibold">提交宣传材料草稿</div>
          <input
            className="rounded border border-gray-300 px-3 py-2 text-sm"
            placeholder="材料标题"
            value={createForm.title}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, title: e.target.value }))}
            required
          />
          <select
            className="rounded border border-gray-300 px-3 py-2 text-sm"
            value={createForm.materialType}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, materialType: e.target.value as CreatePromotionMaterialDto['materialType'] }))}
          >
            {PROMOTION_MATERIAL_TYPES.map((item) => (
              <option key={item} value={item}>
                {formatEnumLabel(item, PROMOTION_MATERIAL_TYPE_LABELS, '未知类型')}
              </option>
            ))}
          </select>
          <input
            className="rounded border border-gray-300 px-3 py-2 text-sm"
            placeholder="关联项目编码（可选）"
            value={createForm.projectCode ?? ''}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, projectCode: e.target.value }))}
          />
          <input
            className="rounded border border-gray-300 px-3 py-2 text-sm"
            placeholder="OSS 内容 Key（可选）"
            value={createForm.contentOssKey ?? ''}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, contentOssKey: e.target.value }))}
          />
          <textarea
            className="min-h-28 rounded border border-gray-300 px-3 py-2 text-sm lg:col-span-2"
            placeholder="正文内容"
            value={createForm.contentText ?? ''}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, contentText: e.target.value }))}
          />
          <textarea
            className="min-h-20 rounded border border-gray-300 px-3 py-2 text-sm lg:col-span-2"
            placeholder="摘要"
            value={createForm.summary ?? ''}
            onChange={(e) => setCreateForm((prev) => ({ ...prev, summary: e.target.value }))}
          />
          <div className="lg:col-span-2 flex justify-end">
            <button
              type="submit"
              className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              创建草稿
            </button>
          </div>
        </form>
      )}

      {error && <div className="rounded bg-red-50 px-4 py-3 text-sm text-red-700">{error.message}</div>}

      <div className="grid gap-6 xl:grid-cols-[1.35fr_0.9fr]">
        <div className="overflow-x-auto rounded-lg border bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">材料</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">类型</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">状态</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">风险</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">提交时间</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">加载中…</td>
                </tr>
              ) : materials.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">暂无材料</td>
                </tr>
              ) : (
                materials.map((item) => {
                  const actions = NEXT_STATUS_MAP[item.status];
                  return (
                    <tr
                      key={item.id}
                      className={`border-t ${selectedMaterialId === item.id ? 'bg-slate-50' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setSelectedMaterialId(item.id)}
                          className="text-left"
                        >
                          <div className="font-medium">{item.title}</div>
                          <div className="mt-1 text-xs text-muted-foreground">{item.projectCode || item.summary || '未关联项目'}</div>
                        </button>
                      </td>
                      <td className="px-4 py-3">{formatEnumLabel(item.materialType, PROMOTION_MATERIAL_TYPE_LABELS, '未知类型')}</td>
                      <td className="px-4 py-3">{formatEnumLabel(item.status, PROMOTION_MATERIAL_STATUS_LABELS, '未知状态')}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${riskTone(item.riskLevel)}`}>
                          {item.riskLevel ? formatEnumLabel(item.riskLevel, RISK_LEVEL_LABELS, '未知') : '未扫描'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{fmtDateTime(item.submittedAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          {canSubmitMaterial && actions.includes('submit') && (
                            <button
                              onClick={() => { void handleMaterialAction(item, 'submit'); }}
                              className="rounded bg-slate-900 px-2 py-1 text-xs text-white hover:bg-slate-700"
                            >
                              提交审核
                            </button>
                          )}
                          {canApprove && actions.includes('claim') && (
                            <button
                              onClick={() => { void handleMaterialAction(item, 'claim'); }}
                              className="rounded bg-blue-700 px-2 py-1 text-xs text-white hover:bg-blue-600"
                            >
                              领取审核
                            </button>
                          )}
                          {canWithdrawMaterial && actions.includes('withdraw') && (
                            <button
                              onClick={() => { void handleMaterialAction(item, 'withdraw'); }}
                              className="rounded bg-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-300"
                            >
                              撤回
                            </button>
                          )}
                          {canApprove && actions.includes('review') && (
                            <button
                              onClick={() => {
                                setSelectedMaterialId(item.id);
                                setReviewForm((prev) => ({ ...prev, expectedVersion: item.version }));
                              }}
                              className="rounded bg-emerald-700 px-2 py-1 text-xs text-white hover:bg-emerald-600"
                            >
                              审核裁决
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-4">
          <div className="rounded-lg border bg-white p-5 shadow-sm">
            <div className="text-lg font-semibold">审核详情</div>
            {!selectedMaterial ? (
              <div className="mt-4 text-sm text-muted-foreground">选择左侧材料后查看命中词和审核记录。</div>
            ) : (
              <div className="mt-4 space-y-4">
                <div>
                  <div className="font-medium">{selectedMaterial.title}</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {formatEnumLabel(selectedMaterial.materialType, PROMOTION_MATERIAL_TYPE_LABELS, '未知类型')}
                    {' · '}
                    {formatEnumLabel(selectedMaterial.status, PROMOTION_MATERIAL_STATUS_LABELS, '未知状态')}
                    {' · '}版本 {selectedMaterial.version}
                  </div>
                  <div className="mt-3 rounded-md bg-slate-50 p-3 text-sm text-slate-700">
                    {selectedMaterial.contentText || selectedMaterial.summary || selectedMaterial.contentOssKey || '无文本内容'}
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium">敏感词命中</div>
                  <div className="mt-2 space-y-2">
                    {hits.length === 0 ? (
                      <div className="text-sm text-muted-foreground">暂无命中记录</div>
                    ) : (
                      hits.map((item) => (
                        <div key={item.id} className="rounded-md border border-slate-200 p-3 text-sm">
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-medium">{item.matchedTerm}</div>
                            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${riskTone(item.severity)}`}>
                              {formatEnumLabel(item.severity, RISK_LEVEL_LABELS, '未知')}
                            </span>
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            count={item.occurrenceCount} · batch={item.scanBatchId}
                          </div>
                          <div className="mt-2 text-xs text-slate-600">{item.contextSnippet || '无上下文片段'}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium">审核记录</div>
                  <div className="mt-2 space-y-2">
                    {reviews.length === 0 ? (
                      <div className="text-sm text-muted-foreground">暂无审核记录</div>
                    ) : (
                      reviews.map((item) => (
                        <div key={item.id} className="rounded-md border border-slate-200 p-3 text-sm">
                          <div className="flex items-center justify-between">
                            <div className="font-medium">{formatEnumLabel(item.reviewResult, PROMOTION_REVIEW_RESULT_LABELS, '未知结果')}</div>
                            <div className="text-xs text-muted-foreground">{fmtDateTime(item.reviewedAt)}</div>
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {formatEnumLabel(item.reviewerRole, PROMOTION_REVIEWER_ROLE_LABELS, '未知角色')}
                            {' · '}
                            {formatEnumLabel(item.fromStatus, PROMOTION_MATERIAL_STATUS_LABELS, '未知状态')}
                            {' → '}
                            {formatEnumLabel(item.toStatus, PROMOTION_MATERIAL_STATUS_LABELS, '未知状态')}
                          </div>
                          <div className="mt-2 text-slate-700">{item.comment || '无备注'}</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {selectedMaterial && canApprove && selectedMaterial.status === 'under_review' && (
            <div className="rounded-lg border bg-white p-5 shadow-sm">
              <div className="text-lg font-semibold">提交审核裁决</div>
              <div className="mt-3 space-y-3">
                <select
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  value={reviewForm.reviewerRole}
                  onChange={(e) => setReviewForm((prev) => ({ ...prev, reviewerRole: e.target.value as RecordPromotionReviewDto['reviewerRole'] }))}
                >
                  {PROMOTION_REVIEWER_ROLES.map((item) => (
                    <option key={item} value={item}>
                      {formatEnumLabel(item, PROMOTION_REVIEWER_ROLE_LABELS, '未知角色')}
                    </option>
                  ))}
                </select>
                <select
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  value={reviewForm.reviewResult}
                  onChange={(e) => setReviewForm((prev) => ({ ...prev, reviewResult: e.target.value as RecordPromotionReviewDto['reviewResult'] }))}
                >
                  <option value="approved">{PROMOTION_REVIEW_RESULT_LABELS.approved}</option>
                  <option value="changes_requested">{PROMOTION_REVIEW_RESULT_LABELS.changes_requested}</option>
                  <option value="rejected">{PROMOTION_REVIEW_RESULT_LABELS.rejected}</option>
                </select>
                <textarea
                  className="min-h-24 w-full rounded border border-gray-300 px-3 py-2 text-sm"
                  placeholder="审核备注"
                  value={reviewForm.comment ?? ''}
                  onChange={(e) => setReviewForm((prev) => ({ ...prev, comment: e.target.value }))}
                />
                <div className="flex justify-end">
                  <button
                    onClick={() => { void handleReview(selectedMaterial); }}
                    className="rounded bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600"
                  >
                    提交裁决
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
